
import React, { useState } from 'react';
import { getAISuggestion } from '../services/geminiService';
import { AIPromptResponse } from '../types';
import { Sparkles, Loader2, RefreshCcw } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

const AISection: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState<AIPromptResponse | null>(null);
  const [selectedIngredient, setSelectedIngredient] = useState('Mango de hilacha');

  const ingredients = ['Mango de hilacha', 'Cáscara de Piña', 'Semillas de Papaya', 'Mermas de Banano'];

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const res = await getAISuggestion(selectedIngredient);
      setSuggestion(res);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-stone-900 text-stone-100 rounded-2xl p-8 my-12 shadow-xl border border-stone-800">
      <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-6">
        <div className="max-w-md">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="text-amber-400 w-5 h-5" />
            <span className="text-amber-400 text-xs font-bold tracking-widest uppercase">Propuesta IA de Temporada</span>
          </div>
          <h2 className="text-3xl font-bold mb-3 text-white">Innovación de Residuo Cero</h2>
          <p className="text-stone-400 text-sm leading-relaxed">
            Nuestra IA optimiza mermas e ingredientes locales para crear nutrición divertida. Selecciona un ingrediente y descubre la magia.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 justify-center">
          {ingredients.map((ing) => (
            <button
              key={ing}
              onClick={() => setSelectedIngredient(ing)}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-all duration-300 border ${
                selectedIngredient === ing 
                ? 'bg-amber-400 text-stone-900 border-amber-400' 
                : 'bg-transparent text-stone-400 border-stone-700 hover:border-stone-500'
              }`}
            >
              {ing}
            </button>
          ))}
          <button
            onClick={handleGenerate}
            disabled={loading}
            className="ml-4 bg-white text-stone-900 px-6 py-2 rounded-full text-xs font-bold flex items-center gap-2 hover:bg-stone-200 transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
            {loading ? 'Consultando Experto...' : 'Generar Sugerencia'}
          </button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {loading ? (
          <motion.div 
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center py-12 border border-dashed border-stone-700 rounded-xl bg-stone-800/20"
          >
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-amber-400 mb-4" />
            <p className="text-stone-400 text-sm">Nuestro experto en gastronomía está creando algo especial...</p>
          </motion.div>
        ) : suggestion ? (
          <motion.div 
            key="suggestion"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid md:grid-cols-2 gap-8"
          >
            <div className="bg-stone-800/50 p-6 rounded-xl border border-stone-700">
              <h4 className="text-amber-400 text-sm font-bold mb-1 uppercase tracking-tighter">Nuevo Producto</h4>
              <h3 className="text-2xl font-bold mb-3 text-white">{suggestion.name}</h3>
              <p className="text-stone-300 italic mb-4">"{suggestion.description}"</p>
              <div className="flex items-center gap-2 text-emerald-400 text-sm font-semibold">
                <span className="bg-emerald-400/10 px-2 py-0.5 rounded border border-emerald-400/20">Nutrición:</span>
                {suggestion.benefit}
              </div>
            </div>
            <div className="flex flex-col justify-center">
              <div className="bg-stone-800/30 p-6 rounded-xl border border-dashed border-stone-600">
                <h4 className="text-stone-400 text-xs font-bold mb-2 uppercase tracking-widest">Análisis de Costo-Beneficio</h4>
                <p className="text-stone-300 text-sm leading-relaxed">{suggestion.reason}</p>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div 
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 border border-dashed border-stone-700 rounded-xl bg-stone-800/20"
          >
            <p className="text-stone-500 text-sm">Elige un insumo para que nuestro experto sugiera la rotación estacional.</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AISection;
